import React, { useEffect, useState } from "react";
import { Form, Input, DatePicker, Select } from "antd";
import { connect, useSelector } from "react-redux";
import FormItem from "components/fields/FormItem";
import {
  getOvertimeDetails,
  doOvertimeCreate,
  doOvertimeUpdate,
  doOvertimeDelete,
} from "../../../../redux/overtime/overtimeState";
import CrudButtons from "components/button/crudbuttons";
import dayjs from "dayjs";
import {
  DATE_FORMAT_MDY,
  TIME_FORMAT_24HM,
  DATE_FORMAT,
  TIME_FORMAT,
  MODULE_OVERTIME,
} from "variables/constants";
import { requiredField } from "utils/crudSagaHelper";
import TextArea from "antd/es/input/TextArea";

const { RangePicker } = DatePicker;
const MAX_HOURS = 16;

const OvertimeForm = ({
  getRecord,
  doCreate,
  doUpdate,
  recordData,
  onCancel,
  submitLabel,
  mode,
  timeCard,
  recordId,
}) => {
  const [form] = Form.useForm();
  const authUser = useSelector((state) => state.auth.user);

  const [rangeValue, setRangeValue] = useState([]);
  const [computedHours, setComputedHours] = useState(0);
  const [originalMaxHours, setOriginalMaxHours] = useState(MAX_HOURS);

  useEffect(() => {
    getRecord(recordId);
  }, [getRecord, recordId]);

  useEffect(() => {
    if (recordData) {
      form.resetFields();
      const { user: user, ...rest } = recordData;

      let start = timeCard?.overtime_start || null;
      let end = timeCard?.overtime_end || null;

      if (!start || !end) {
        start = recordData?.ots_date_from ? dayjs(recordData.ots_date_from) : null;
        end = recordData?.ots_date_to ? dayjs(recordData.ots_date_to) : null;
      }

      let startDate = null;
      let endDate = null;

      if (start && end) {
        startDate = dayjs(start);
        endDate = dayjs(end);

        const overtimeHours = endDate.diff(startDate, "minute") / 60;

        setOriginalMaxHours(overtimeHours);  
        setRangeValue([startDate, endDate]);
        setComputedHours(overtimeHours);
      }

      const initialValues = {
        ...rest,
        ...user?.userDetail,
        ots_user_id: recordData?.ots_user_id ?? null,
        ots_date_range: startDate && endDate ? [startDate, endDate] : [],
        ots_approved_hours: recordData?.ots_approved_hours ?? null,
        user_details: {
          ude_first_name: user?.user_detail?.ude_first_name ?? "",
          ude_last_name: user?.user_detail?.ude_last_name ?? "",
        },
      };

      form.setFieldsValue(initialValues);
    }
  }, [recordData, form, timeCard]);

  const onSubmit = (values) => {
    const payload = {
      ots_id: values.ots_id ?? 0,
      ots_user_id: authUser?.id,
      ots_status_id: 66,
      ots_timecard_id: timeCard?.tmc_id ?? null,
      ots_approved_hours: computedHours,
      ots_reason: values.ots_reason ?? null,
    };

    if (values.ots_date_range?.length === 2) {
      payload.ots_date_from = values.ots_date_range[0].format(
        `${DATE_FORMAT} ${TIME_FORMAT}`
      );
      payload.ots_date_to = values.ots_date_range[1].format(
        `${DATE_FORMAT} ${TIME_FORMAT}`
      );
    }

    recordData?.ots_id > 0 ? doUpdate(payload) : doCreate(payload);
    onCancel?.();
  };

  const handleRangeChange = (dates) => {
    if (!dates || dates.length !== 2) {
      setComputedHours(0);
      setRangeValue([]);
      form.setFieldsValue({ ots_date_range: [] });
      return;
    }

    let [start, end] = dates;

    const durationHours = end.diff(start, "hour", true);
    if (durationHours > originalMaxHours) {
      end = start.add(originalMaxHours, "hour");
    }

    let hours = end.diff(start, "minute") / 60;
    if (hours < 0) hours = 0;
    if (hours > originalMaxHours) hours = originalMaxHours;

    setRangeValue([start, end]);
    setComputedHours(hours);
    form.setFieldsValue({ ots_date_range: [start, end] });
  };

  return (
    <Form form={form} layout="vertical" className={mode !== "view" ? "form" : ""} onFinish={onSubmit}>
      <FormItem name="ots_id" hidden>
        <Input type="hidden" />
      </FormItem>

      <div className="grid grid-cols-3 gap-y-2 gap-x-4">
        {authUser?.user_level === 1 && mode !== "view" && (
          <div className="col-span-3 mb-2">
            <FormItem className="font-medium mb-2" label="Employee" required>
              <FormItem
                name="ots_user_id"
                noStyle
                rules={[requiredField]}
                initialValue={recordData?.ots_user_id || null}
              >
                <Select
                  allowClear
                  showSearch
                  className="input-field"
                  placeholder="Select Employee"
                  value={recordData?.ots_user_id ?? undefined}
                  options={recordData?.employeeList}
                  onChange={(value) =>
                    form.setFieldsValue({ ots_user_id: value })
                  }
                />
              </FormItem>
            </FormItem>
          </div>
        )}

        <div className="col-span-3 mb-2">
          <div className="grid grid-cols-4 gap-2 w-full">
            <FormItem
              name="ots_date_range"
              label="Overtime Duration"
              className="font-medium mb-2 col-span-3"
              rules={[requiredField]}
            >
              {mode !== "view" ? (
                <RangePicker
                  className="input-field w-full"
                  showTime={{ format: TIME_FORMAT_24HM }}
                  format={`${DATE_FORMAT_MDY} ${TIME_FORMAT_24HM}`}
                  value={rangeValue}
                  onChange={handleRangeChange}
                />
              ) : (
                <label className="text-base label-value">
                  {rangeValue
                    ? `${rangeValue[0].format(`${DATE_FORMAT_MDY} ${TIME_FORMAT_24HM}`)} - ${rangeValue[1].format(`${DATE_FORMAT_MDY} ${TIME_FORMAT_24HM}`)}`
                    : ""}
                </label>
              )}
            </FormItem>

            <FormItem label="Hours" className="font-medium mb-2 col-span-1">
              {mode !== "view" ? (
                <Input value={computedHours} readOnly className="input-field w-full" />
              ) : (
                <label className="text-base label-value dark:text-white">{computedHours}</label>
              )}
            </FormItem>
          </div>
          <FormItem
              className="font-medium mb-2 col-span-3"
              name="ots_reason"
              label="Reason"
              rules={[requiredField]}
            >
              <TextArea
                id="ots_reason"
                className="input-field"
                maxLength={255}
                placeholder="State a reason..."
              />
            </FormItem>
        </div>
      </div>

      <div className="w-full flex justify-end">
        {mode === "edit" && (
          <CrudButtons
            mode={mode}
            recordId={recordId}
            isModal
            modalId={recordData?.ots_id > 0 ? "edit_overtime" : "add_overtime"}
            cancelLabel="Cancel"
            doHideModal={onCancel}
            submitLabel={submitLabel}
            moduleId={MODULE_OVERTIME}
          />
        )}
      </div>
    </Form>
  );
};

const mapStateToProps = (state) => ({
  recordData: state.overtime.overtimeDetails?.data,
});

const mapDispatchToProps = (dispatch) => ({
  getRecord: (id) => dispatch(getOvertimeDetails(id)),
  doCreate: (data) => dispatch(doOvertimeCreate(data)),
  doUpdate: (data) => dispatch(doOvertimeUpdate(data)),
  doDelete: (data) => dispatch(doOvertimeDelete(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OvertimeForm);
